import { GroupedTable } from "@/components/GroupedTable/GroupedTable";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { truncateLength } from "@/components/app-layout/consts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Row } from "@/components/ui/flex";
import { Skeleton, TableSkeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ImpersonateUserButton } from "@/components/user/ImpersonateUserButton";
import { UserRoleSelection } from "@/components/user/RoleSelection";
import { useGlobalGroupsData } from "@/data/use-global-groups-data";
import { useGlobalUserData } from "@/data/use-users-data";
import { GetUsersDto } from "@/lib/gen-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faTrash,
  faUnlink,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { toast } from "sonner";

export default function GlobalAdminUserPage() {
  const { userUuid } = useParams({ strict: false }) as { userUuid?: string };
  const navigate = useNavigate();

  const { user, updateUser, isLoading, deleteUser } =
    useGlobalUserData(userUuid);
  const {
    groups,
    isLoading: groupsLoading,
    unlinkGroup,
  } = useGlobalGroupsData(userUuid);

  const handleRoleChange = (role: string) => {
    if (!userUuid) return;
    const roleValue = role as unknown as GetUsersDto["data"][number]["role"];
    updateUser({ body: { role: roleValue } });
  };

  const handleUnlink = async (groupUuid: string) => {
    await unlinkGroup(groupUuid);
  };

  const handleDeleteUser = async () => {
    if (!userUuid) return;

    try {
      await deleteUser(userUuid);
      navigate({ to: "/admin/users", replace: true });
      toast.success(`User ${user?.name} (${user?.email}) deleted successfully`);
    } catch {
      toast.error("Failed to delete user");
    }
  };

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Global User Management</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <Row className="items-center justify-between">
          <Row className="items-center gap-2">
            {user && <ImpersonateUserButton user={user} />}
            {user && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="default"
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    title="Delete user permanently"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Delete {user.name || user.email}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete
                      this user from the system.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={handleDeleteUser}
                      asChild
                    >
                      <Button size="sm">Delete</Button>
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </Row>
        </Row>

        <dl className="grid grid-cols-[100px_1fr] items-center gap-y-2">
          <div className="contents">
            <dt className="text-sm font-semibold text-gray-500">Name</dt>
            {isLoading && <Skeleton className="h-4 w-1/3" />}
            {!isLoading && user && <dd>{user.name}</dd>}
          </div>
          <div className="contents">
            <dt className="text-sm font-semibold text-gray-500">Email</dt>
            {isLoading && <Skeleton className="h-4 w-1/3" />}
            {!isLoading && user && <dd>{user.email}</dd>}
          </div>
          <div className="contents">
            <dt className="text-sm font-semibold text-gray-500">Role</dt>
            {isLoading && <Skeleton className="h-4 w-1/3" />}
            {!isLoading && user && (
              <dd>
                <UserRoleSelection
                  value={user.role}
                  onChange={handleRoleChange}
                />
              </dd>
            )}
          </div>
        </dl>

        <h2 className="text-xl font-bold">Groups</h2>

        {groupsLoading && <TableSkeleton />}
        {!groupsLoading && (
          <GroupedTable
            data={[
              {
                groupId: "groups",
                label: "Groups",
                items: groups,
              },
            ]}
            columns={[
              {
                key: "name",
                label: "Name",
              },
              {
                key: "slug",
                label: "Slug",
              },
            ]}
            renderCell={{
              name: (_, row) =>
                row.name.length > truncateLength ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{`${row.name.slice(0, truncateLength)}…`}</span>
                      </TooltipTrigger>
                      <TooltipContent>{row.name}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <span>{row.name}</span>
                ),
              slug: (_, row) =>
                row.slug.length > truncateLength ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{`${row.slug.slice(0, truncateLength)}…`}</span>
                      </TooltipTrigger>
                      <TooltipContent>{row.slug}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <span>{row.slug}</span>
                ),
            }}
            renderActions={({ row }) => (
              <Row className="items-center gap-2">
                <Link
                  to="/admin/groups/$groupUuid"
                  params={{ groupUuid: row.uuid }}
                >
                  <FontAwesomeIcon icon={faEye} />
                </Link>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hover:bg-destructive hover:text-destructive-foreground"
                      title="Remove"
                    >
                      <FontAwesomeIcon icon={faUnlink} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Unlink Group?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to unlink {user?.name}{" "}
                        {user?.email} from the {row.name} group?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => handleUnlink(row.uuid)}
                        asChild
                      >
                        <Button size="sm">Unlink</Button>
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </Row>
            )}
          />
        )}
      </PageBody>
    </Page>
  );
}
